import { forwardRef, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';
dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
import { UPDATE_DATE } from '@/utils/hooks/datepicker/actions';
import { useDatepicker } from '@/utils/hooks/datepicker/useDatepicker';
import useErrorTranslation from '@/utils/hooks/language/useErrorTranslation';
import useSocialMediaEvents from '@/utils/hooks/scheduler/useSocialMediaEvents';
import useUser from '@/utils/hooks/swr/useUser';

import { post, put } from '@/utils/client';

import datepickerDateFormat from '@/lib/datepicker/dateFormat';

import Button from '@/components/base/Button';
import Datepicker from '@/components/base/Datepicker';
import SingleDatepickerInput from '@/components/base/Datepicker/SingleDatepickerInput';
// Components
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Input from '@/components/base/Input';
import Label from '@/components/base/Label';
import Modal from '@/components/base/Modal';
import InputActionTag from '@/components/composite/Form/Input/InputActionTag';

// Utils
import { SCHEDULER_EVENTS } from '@/routes/apiEndpoints';

const ContentDatepicker = forwardRef(({ id, name, date, ...restProps }, ref) => {
    const { dispatch } = useDatepicker();
    useEffect(() => {
        dispatch({
            type: UPDATE_DATE,
            payload: date
        });
    }, [date, dispatch]);

    return <SingleDatepickerInput id={id} name={name} {...restProps} ref={ref} />;
});

ContentDatepicker.displayName = 'ContentDatepicker';

ContentDatepicker.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired
};

const FormSocialMediaEvent = ({ toggle, event }) => {
    const { t } = useTranslation('scheduler');
    const { t_error } = useErrorTranslation();
    const { user } = useUser();
    const { mutateSocialMediaEvents } = useSocialMediaEvents({ revalidateIfStale: false });

    const [formState, setFormState] = useState({
        isSubmitting: false,
        data: {
            id: event?.id ?? null,
            title: event?.title ?? '',
            start_date: event?.start_date ? event?.start_date : dayjs().utc().format('YYYY-MM-DD'),
            end_date: event?.end_date ? event?.end_date : dayjs().utc().format('YYYY-MM-DD'),
            hashtags: event?.hashtags ?? []
        }
    });

    const onSubmit = async () => {
        setFormState((prevState) => ({
            ...prevState,
            error: null,
            isSubmitting: true
        }));

        try {
            const dataToSend = {
                title: formState.data.title,
                hashtags: formState.data.hashtags,
                start: formState.data.start_date,
                end: formState.data.end_date
            };

            if (formState.data.id) {
                await put(`${SCHEDULER_EVENTS}/${formState.data.id}`, dataToSend);
            } else {
                await post(SCHEDULER_EVENTS, dataToSend);
            }

            await mutateSocialMediaEvents();
            toggle();
        } catch (e) {
            if (e.status === 422) {
                const errorsFields = e.response?.data?.errors;
                // Parse error object, get first error and display global error
                if (errorsFields) {
                    Object.keys(errorsFields).map((field) => {
                        const error_type = errorsFields[field][0];
                        setFormState((prevState) => ({
                            ...prevState,
                            isSubmitting: false,
                            error: t_error('scheduler:custom_events.modal.error_' + error_type)
                        }));
                    });
                } else {
                    setFormState((prevState) => ({
                        ...prevState,
                        isSubmitting: false,
                        error: t('errors:error_occured_submit_data')
                    }));
                }
            } else {
                setFormState((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: t('errors:error_occured_submit_data')
                }));
            }
        }
    };

    return (
        <>
            <Modal.Body>
                {formState.error ? (
                    <InfoBanner
                        className="mb-4"
                        title={t('errors:error')}
                        message={formState.error}
                        onClose={() => {
                            setFormState((prevState) => ({
                                ...prevState,
                                error: null
                            }));
                        }}
                    />
                ) : null}
                <Modal.Title>
                    {formState.data.id
                        ? t('custom_events.modal.edit_event')
                        : t('custom_events.modal.create_event')}
                </Modal.Title>
                <Modal.Content className="pt-8">
                    <div className="flex flex-col space-y-5">
                        <div>
                            <Label required htmlFor="title_event">
                                {t('custom_events.modal.title_event')}
                            </Label>
                            <Input
                                id="title_event"
                                name="title_event"
                                type="text"
                                value={formState.data.title}
                                placeholder={t('custom_events.modal.title_input_placeholder')}
                                onChange={(e) => {
                                    setFormState((prevState) => ({
                                        ...prevState,
                                        data: {
                                            ...prevState.data,
                                            title:
                                                e.target.value?.trim() === ''
                                                    ? e.target.value?.trim()
                                                    : e.target.value
                                        }
                                    }));
                                }}
                            />
                        </div>
                        <div className="z-[2] flex flex-col space-y-5 xs:flex-row xs:space-x-5 xs:space-y-0">
                            <div className="relative w-full">
                                <Label required htmlFor="start_date_event">
                                    {t('custom_events.modal.start_date_event')}
                                </Label>
                                <Datepicker
                                    date={formState.data.start_date}
                                    hasBorder
                                    options={{
                                        placement: 'bottom-start',
                                        dateFormat:
                                            datepickerDateFormat[user.settings.date_format] ??
                                            'Y/m/d',
                                        parseDate: (dateStr) => {
                                            return dayjs(dateStr).utc().toDate();
                                        }
                                    }}
                                    onChange={({ date }) => {
                                        const dateStart = typeof date === 'object' ? date[0] : date;
                                        const dateStartFormatted =
                                            dayjs(dateStart).format('YYYY-MM-DD');
                                        setFormState((prevState) => ({
                                            ...prevState,
                                            data: {
                                                ...prevState.data,
                                                start_date: dateStartFormatted,
                                                end_date:
                                                    dateStartFormatted > prevState.data.end_date
                                                        ? dateStartFormatted
                                                        : prevState.data.end_date
                                            }
                                        }));
                                    }}>
                                    <ContentDatepicker
                                        id="start_date_event"
                                        name="start_date_event"
                                        date={formState.data.start_date}
                                    />
                                </Datepicker>
                            </div>
                            <div className="relative w-full">
                                <Label htmlFor="end_date_event">
                                    {t('custom_events.modal.end_date_event')}
                                </Label>
                                <Datepicker
                                    date={formState.data.end_date}
                                    hasBorder
                                    options={{
                                        placement: 'bottom-start',
                                        dateFormat:
                                            datepickerDateFormat[user.settings.date_format] ??
                                            'Y/m/d',
                                        parseDate: (dateStr) => {
                                            return dayjs(dateStr).utc().toDate();
                                        }
                                    }}
                                    onChange={({ date }) => {
                                        const dateEnd = typeof date === 'object' ? date[0] : date;
                                        const dateEndFormatted =
                                            dayjs(dateEnd).format('YYYY-MM-DD');
                                        setFormState((prevState) => ({
                                            ...prevState,
                                            data: {
                                                ...prevState.data,
                                                start_date:
                                                    dateEndFormatted < prevState.data.start_date
                                                        ? dateEndFormatted
                                                        : prevState.data.start_date,
                                                end_date: dateEndFormatted
                                            }
                                        }));
                                    }}>
                                    <ContentDatepicker
                                        id="end_date_event"
                                        name="end_date_event"
                                        date={formState.data.end_date}
                                    />
                                </Datepicker>
                            </div>
                        </div>
                        <div>
                            <Label className="mb-4 flex items-center" htmlFor="hashtag_event">
                                <Icon icon="hashtag" className="text-sm text-icoBlue" />
                                <span className="ml-2.5 text-icoBlack dark:text-white">
                                    {t('custom_events.modal.hashtags_event')}
                                </span>
                            </Label>
                            <InputActionTag
                                id="hashtag_event"
                                name="hashtag_event"
                                placeholder={t('custom_events.modal.hashtags_input_placeholder')}
                                shouldClearSpace
                                additionalCheckOnEvent={(e) => e.target.value?.trim() !== '#'}
                                triggerEventList={[' ', 'space']}
                                onAddItem={(item) => {
                                    const itemFormatted = item[0] === '#' ? item : `#${item}`;
                                    if (!formState.data.hashtags.includes(itemFormatted)) {
                                        setFormState((prevState) => ({
                                            ...prevState,
                                            data: {
                                                ...prevState.data,
                                                hashtags: [
                                                    ...prevState.data.hashtags,
                                                    itemFormatted
                                                ]
                                            }
                                        }));
                                    }
                                }}
                                onDeleteItem={(item) => {
                                    setFormState((prevState) => ({
                                        ...prevState,
                                        data: {
                                            ...prevState.data,
                                            hashtags: prevState.data.hashtags.filter(
                                                (hashtag) => hashtag !== item
                                            )
                                        }
                                    }));
                                }}
                                tagList={formState.data.hashtags}
                            />
                        </div>
                    </div>
                </Modal.Content>
            </Modal.Body>
            <Modal.Footer divider={false}>
                <button
                    onClick={toggle}
                    className="font-GMedium text-xs text-icoGray-500 underline">
                    {t('commons:cancel')}
                </button>
                <Button
                    onClick={onSubmit}
                    isLoading={formState.isSubmitting}
                    disabled={
                        formState.data.title === '' ||
                        formState.data.start_date === '' ||
                        formState.data.end_date === ''
                    }>
                    {formState.data.id ? t('commons:edit') : t('commons:create')}
                </Button>
            </Modal.Footer>
        </>
    );
};

FormSocialMediaEvent.propTypes = {
    toggle: PropTypes.func.isRequired
};

const CreateSocialMediaEventModal = ({ isOpen, toggle, event = null }) => {
    return (
        <Modal open={isOpen} onDismiss={toggle}>
            <FormSocialMediaEvent toggle={toggle} event={event} />
        </Modal>
    );
};

FormSocialMediaEvent.propTypes = {
    isOpen: PropTypes.bool,
    toggle: PropTypes.func
};

export default CreateSocialMediaEventModal;
